/**
 * SmartHoldTile.jsx — "Hold or Fire?" recommendation card.
 *
 * Reads the existing widget snapshot (which already contains MAD bucket data)
 * and surfaces a one-line recommendation: HOLD vs FIRE based on current
 * depletion vs MAD threshold + projected days-until-trigger from recent slope.
 *
 * No new backend endpoint needed — pure client-side derivation from
 * /api/widget/snapshot which is already cached + tier-public.
 */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { API_URL } from '../config/api';
import { Link } from 'react-router-dom';
import { Droplets, ArrowRight } from 'lucide-react';
import { MadSourcePill } from './MadSourcePill';

/**
 * Compute days-until-trigger from the current depletion + dry-down slope.
 * Returns { recommendation: 'hold'|'fire'|'unknown', daysUntil, summary }
 *
 * Defensive against snapshot schema drift — checks ~6 different shapes
 * before giving up. Falls back to an Ecowitt-direct read of CH1 (DRY probe)
 * when the widget snapshot doesn't expose MAD bucket data.
 */
function deriveRecommendation(snapshot, ecowittLive, statsCh1) {
  // Canonical MAD object — single shape across endpoints (DESIGN_PRINCIPLES §47).
  // Forward the whole sub-object so new fields (source, probe_vwc_pct, etc.)
  // propagate without surgery here.
  const madObj =
    snapshot?.ticker?.mad ??
    snapshot?.mad ??
    null;
  let depletion =
    madObj?.depletion_pct ??
    snapshot?.mad_depletion_pct ??
    snapshot?.today?.mad_depletion_pct ??
    snapshot?.lawn_conditions?.mad_pct ??
    null;
  let threshold =
    madObj?.threshold_pct ??
    madObj?.mad_threshold_pct ??
    snapshot?.mad_threshold_pct ??
    50;

  // 2) Fallback: derive from CH1 (DRY) moisture directly.
  // Depletion ≈ 100 - moisture% when probe is calibrated 0=dry, 100=field cap.
  if (depletion == null && ecowittLive) {
    const dry = (ecowittLive.soil_channels || []).find((c) => c.channel === 1);
    const moisture = dry?.moisture_pct;
    if (moisture != null) {
      depletion = Math.max(0, 100 - Number(moisture));
    }
  }

  if (depletion == null) return { recommendation: 'unknown' };

  // Slope = avg %/day depletion increase from /api/ecowitt/history/stats CH1.
  // Fallback to a conservative 2%/day if stats unavailable.
  const slopePctPerDay = Math.abs(statsCh1?.slope_pct_per_day ?? 2);
  const headroom = Math.max(0, threshold - depletion);
  const daysUntil = slopePctPerDay > 0.1 ? Math.round(headroom / slopePctPerDay) : null;

  let recommendation = 'fire';
  let summary = '';
  if (depletion < threshold - 5) {
    recommendation = 'hold';
    summary = daysUntil != null
      ? `Hold tonight · MAD ${Math.round(depletion)}% · trigger ~${threshold}% · projected fire in ~${daysUntil}d`
      : `Hold tonight · MAD ${Math.round(depletion)}% < ${threshold}% trigger`;
  } else if (depletion < threshold) {
    recommendation = 'fire-soon';
    summary = `Fire window opens this week · MAD ${Math.round(depletion)}% closing on ${threshold}%`;
  } else {
    recommendation = 'fire';
    summary = `Fire tonight · MAD ${Math.round(depletion)}% ≥ ${threshold}% trigger`;
  }
  return { recommendation, daysUntil, summary, depletion, threshold, mad: madObj };
}

const TONE = {
  hold:       { bg: 'bg-emerald-50 dark:bg-emerald-950/30', ring: 'border-emerald-300 dark:border-emerald-800', text: 'text-emerald-800 dark:text-emerald-200', label: '✋ Hold' },
  'fire-soon':{ bg: 'bg-amber-50 dark:bg-amber-950/30',     ring: 'border-amber-300 dark:border-amber-800',     text: 'text-amber-800 dark:text-amber-200',     label: '⏳ Fire soon' },
  fire:       { bg: 'bg-rose-50 dark:bg-rose-950/30',       ring: 'border-rose-300 dark:border-rose-800',       text: 'text-rose-800 dark:text-rose-200',       label: '💧 Fire tonight' },
  unknown:    { bg: 'bg-muted',                             ring: 'border-border',                              text: 'text-muted-foreground',                  label: '— No data' },
};

export const SmartHoldTile = () => {
  const [rec, setRec] = useState(null);

  const load = useCallback(async () => {
    try {
      const [snap, ecowitt, stats] = await Promise.all([
        axios.get(`${API_URL}/api/widget/snapshot`, { withCredentials: true }).then((r) => r.data).catch(() => null),
        axios.get(`${API_URL}/api/ecowitt/live`, { withCredentials: true }).then((r) => r.data).catch(() => null),
        axios.get(`${API_URL}/api/ecowitt/history/stats?days=7`, { withCredentials: true }).then((r) => r.data).catch(() => null),
      ]);
      const dryStats = (stats?.channels || []).find((c) => c.channel === 1) || null;
      setRec(deriveRecommendation(snap, ecowitt, dryStats));
    } catch {
      /* auto-hide on failure */
    }
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, 5 * 60 * 1000);  // refresh every 5 min
    return () => clearInterval(id);
  }, [load]);

  if (!rec || rec.recommendation === 'unknown') return null;
  const tone = TONE[rec.recommendation] || TONE.unknown;

  return (
    <div
      className={`rounded-xl border ${tone.ring} ${tone.bg} px-4 py-3`}
      data-testid="smart-hold-tile"
    >
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <Droplets className={`w-5 h-5 ${tone.text}`} />
          <div>
            <div className={`text-[10px] uppercase tracking-widest font-bold ${tone.text} flex items-center gap-2 flex-wrap`}>
              <span>{tone.label} · TurfPulse Smart Hold</span>
              <MadSourcePill mad={rec.mad} size="xs" />
            </div>
            <div className={`text-sm font-semibold ${tone.text}`} data-testid="smart-hold-summary">
              {rec.summary}
            </div>
            {rec.daysUntil != null && rec.recommendation === 'hold' && (
              <div className="text-[10px] text-muted-foreground mt-0.5">
                Don't water tonight — soil still has {Math.round(rec.threshold - rec.depletion)}% of capacity to give. Save the cycle.
              </div>
            )}
          </div>
        </div>
        <Link
          to="/irrigation"
          className={`text-xs font-semibold ${tone.text} hover:underline flex items-center gap-1 shrink-0`}
          data-testid="smart-hold-cta"
        >
          schedule <ArrowRight className="w-3 h-3" />
        </Link>
      </div>
    </div>
  );
};

export default SmartHoldTile;
